import { UAContext } from "@quentin-sommer/react-useragent";
import flat from "flat";
import { LoaderCircle } from "lucide-react";
import React from "react";
import { useTranslation } from "react-i18next";
import TextField from "~/components/Fields/TextField";
import { SheetOrModal } from "~/components/Modal";
import Button from "~/design/ui/Button";
import toastWithStyle from "~/helpers/toastWithStyle";
import { patchResource } from "~/services/api";
import to from "~/utils/to";

function EditUserProfile({ visible, onClose, cookie, user }: { visible: boolean; onClose: () => void; cookie: any; user: any }) {
  const [submitting, setSubmitting] = React.useState(false);
  const { uaResults } = React.useContext(UAContext);
  const { t, i18n } = useTranslation();
  const [data, setData] = React.useState({
    "profile.name.first": user?.profile?.name?.first || "",
    "profile.name.last": user?.profile?.name?.last || "",
  });

  React.useEffect(() => {
    setData({
      "profile.name.first": user?.profile?.name?.first || "",
      "profile.name.last": user?.profile?.name?.last || "",
    });
  }, [user]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setData({ ...data, [name]: value });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    const [err] = await to(patchResource({ resource: "users", resourceId: user._id, token: cookie.token, data: flat.unflatten(data, { safe: true }) }));
    setSubmitting(false);

    if (err) return toastWithStyle({ message: err.response?.data?.message, options: { type: "error" }, uaResults, i18n });

    toastWithStyle({ message: t("modals.editUserProfile.successMessage"), options: { type: "success" }, uaResults, i18n });
    onClose();
  };

  const Content = React.useMemo(() => {
    return (
      <form className="flex flex-col" aria-disabled={submitting} onSubmit={onSubmit}>
        <div className="flex flex-col items-center text-center">
          <h2 className="font-display text-xl font-bold leading-relaxed text-ink">{t("modals.editUserProfile.profile")}</h2>
          <p className="mt-1 max-w-[34ch] text-sm leading-7 text-ink-2">{t("modals.editUserProfile.editYourProfile")}</p>
        </div>

        <div className="mt-5 flex flex-col gap-2">
          <TextField
            label={{ text: t("modals.editUserProfile.firstNameLabel") }}
            input={{
              type: "text",
              name: "profile.name.first",
              value: data["profile.name.first"],
              placeholder: t("modals.editUserProfile.firstNamePlaceholder"),
              onChange: handleInputChange,
            }}
            errorMode="subtitle"
          />

          <TextField
            label={{ text: t("modals.editUserProfile.lastNameLabel") }}
            input={{
              type: "text",
              name: "profile.name.last",
              value: data["profile.name.last"],
              placeholder: t("modals.editUserProfile.lastNamePlaceholder"),
              onChange: handleInputChange,
            }}
            errorMode="subtitle"
          />
        </div>

        <div className="mt-6 flex flex-col gap-2">
          <Button type="submit" size="lg" block disabled={submitting} aria-busy={submitting}>
            {submitting && <LoaderCircle className="size-5 animate-spin" strokeWidth={1.75} />}
            {submitting ? t("modals.deleteUserConfirmation.pleaseWait") : t("modals.editUserProfile.saveChanges")}
          </Button>
          <Button variant="ghost" size="lg" block onClick={onClose}>
            {t("modals.editUserProfile.closeButton")}
          </Button>
        </div>
      </form>
    );
  }, [data, onClose, submitting, t]);

  return (
    <SheetOrModal visible={visible} onClose={onClose} label={t("modals.editUserProfile.profile")}>
      {Content}
    </SheetOrModal>
  );
}

export default EditUserProfile;
